import { action, computed, toJS } from 'mobx';
import { Entity } from '../../core/entity';
import type { IMeasurementViewModel, SceneItemKind } from '../interfaces';
import type { Transparency } from '../interfaces';
import type { ImagesMode, SliceType } from '../modules/images/interfaces';

export interface MeasurementViewModelProps {
  descriptor: string;
  attachments: Array<string>;
  details: unknown;
  label: string;
  color: string;
  highlightColor: string;
  selectionColor: string;
  isVisible: boolean;
  isHighlighted: boolean;
  isSelected: boolean;
  syncSliceDescriptor: SliceType;
  transparency: Transparency;
  isPickable: boolean;
  isTunable: boolean;
  isBeingEdited: boolean;
  isClippable: boolean;
  isContourVisible: boolean;
  kind: SceneItemKind;
  zoomFactor: number;
  imagesMode: ImagesMode;
  imported: boolean;
  isAvailable: boolean;
  phaseId: string | undefined;
  readyForSliding: boolean;
  clDescriptor?: string;
  isLabelColored?: boolean;
}

export class MeasurementViewModel extends Entity<MeasurementViewModelProps> implements IMeasurementViewModel {
  public get descriptor(): string {
    return this.props.descriptor;
  }

  public get attachments(): Array<string> {
    return this.props.attachments;
  }

  @computed
  public get readyForSliding(): boolean {
    return this.props.readyForSliding;
  }

  @computed
  public get transparency(): Transparency {
    return this.props.transparency;
  }

  @computed
  public get label(): string {
    return this.props.label;
  }

  @computed
  public get isLabelColored(): boolean | undefined {
    return this.props.isLabelColored;
  }

  @computed
  public get color(): string {
    return this.props.color;
  }

  @computed
  public get phaseId(): string | undefined {
    return this.props.phaseId;
  }

  @computed
  public get highlightColor(): string {
    return this.props.highlightColor;
  }

  @computed
  public get selectionColor(): string {
    return this.props.selectionColor;
  }

  @computed
  public get syncSliceDescriptor(): SliceType {
    return this.props.syncSliceDescriptor;
  }

  @computed
  public get isVisible(): boolean {
    return this.props.isVisible && this.props.isAvailable;
  }

  @computed
  public get isAvailable(): boolean {
    return this.props.isAvailable;
  }

  @computed
  public get isClippable(): boolean {
    return this.props.isClippable;
  }

  @computed
  public get isContourVisible(): boolean {
    return this.props.isVisible;
  }

  @computed
  public get isHighlighted(): boolean {
    return this.props.isHighlighted;
  }

  @computed
  public get isSelected(): boolean {
    return this.props.isSelected;
  }

  @computed
  public get isPickable(): boolean {
    return this.props.isPickable;
  }

  @computed
  public get isTunable(): boolean {
    return this.props.isTunable;
  }

  @computed
  public get isBeingEdited(): boolean {
    return this.props.isBeingEdited;
  }

  @computed
  public get kind(): SceneItemKind {
    return this.props.kind;
  }

  @computed
  public get zoomFactor(): number {
    return this.props.zoomFactor ?? 0.5;
  }

  @computed
  public get imagesMode(): ImagesMode {
    return this.props.imagesMode;
  }

  @computed
  public get clDescriptor(): string | undefined {
    return this.props.clDescriptor;
  }

  @computed
  public get imported(): boolean {
    return this.props.imported;
  }

  @computed
  public get details(): unknown {
    return this.props.details;
  }

  @action
  public setVisibility(visibility: boolean): void {
    this.props.isVisible = visibility;
  }

  @action
  public setAvailability(availability: boolean): void {
    this.props.isAvailable = availability;
  }

  @action
  public setContourVisibility(visibility: boolean): void {
    this.props.isContourVisible = visibility;
  }

  @action
  public setHighlighting(highlighting: boolean): void {
    this.props.isHighlighted = highlighting;
  }

  @action
  public setSelection(selected: boolean): void {
    this.props.isSelected = selected;
  }

  @action
  public setLabel(label: string): void {
    this.props.label = label;
  }

  @action
  public setDetails(details: unknown): void {
    this.props.details = details;
  }

  public setTransparency(transparency: Transparency): void {
    this.props.transparency = transparency;
  }

  @action
  public setColor(color: string): void {
    this.props.color = color;
  }

  @action
  public setIsPickable(isPickable: boolean): void {
    this.props.isPickable = isPickable;
  }

  @action
  public setIsTunable(isTunable: boolean): void {
    this.props.isTunable = isTunable;
  }

  @action
  public setBeingEdited(isBeingEdited: boolean): void {
    this.props.isBeingEdited = isBeingEdited;
  }

  @action
  public setReadyForSliding(isReady: boolean): void {
    this.props.readyForSliding = isReady;
  }

  public clone(): IMeasurementViewModel {
    return new MeasurementViewModel(toJS(this.props));
  }
}
